import React, { useId, useState } from "react";

// Phones have no hover, so a chart's hover details open in a tap panel
// instead (the SeasonBars pattern, mobileProfileSections.js): each bar,
// point or row is a toggle button (aria-pressed, aria-controls) and one
// panel under (or above) the chart shows the picked item. One item at a
// time; the same button again, or Esc, closes it. The panel stays in the
// page while closed (empty, taking no space), so a screen reader announces
// the first item it opens (aria-live="polite").
//
//   const tap = useTapPanel();
//   <button {...tap.triggerProps(i)} aria-label="Jul 14 vs Team A: 23 points" />
//   <TapPanel id={tap.panelId} open={tap.openKey != null} onClose={tap.close}>
//     {detail for tap.openKey}
//   </TapPanel>
//
// useTapPanel(scope?) -> { openKey, isOpen(key), open(key), toggle(key),
//   close(), panelId, triggerProps(key) }. Keys are any values compared with
//   ===. `scope` (optional) names the data the keys point into (the charts
//   pass their item keys joined): a pick belongs to the scope it was made
//   in, so when the scope changes (another season, another team) the panel
//   reads as closed and never shows, or asks for, an item that is gone.
// TapPanel({ id, open, onClose, children, placement: "below" | "above",
//   bare, className }): `bare` drops the subtle box around the content.

const cx = (...parts) => parts.filter(Boolean).join(" ");

export const useTapPanel = (scope = null) => {
  const [picked, setPicked] = useState({ key: null, scope });
  const panelId = useId();
  const openKey = picked.key !== null && Object.is(picked.scope, scope) ? picked.key : null;
  const open = (key) => setPicked({ key: key ?? null, scope });
  const close = () => setPicked({ key: null, scope });
  const toggle = (key) =>
    setPicked((current) => ({ key: Object.is(current.scope, scope) && current.key === key ? null : key, scope }));
  const triggerProps = (key) => ({
    type: "button",
    "aria-pressed": openKey === key,
    "aria-controls": panelId,
    onClick: () => toggle(key),
    onKeyDown: (e) => {
      if (e.key === "Escape" && openKey !== null) {
        e.preventDefault();
        close();
      }
    },
  });
  return { openKey, isOpen: (key) => openKey === key, open, toggle, close, panelId, triggerProps };
};

// The charts' tap scope: their item keys (or positions) joined, so a new
// list of games, points or axes closes a panel picked on the old one.
export const tapScopeOf = (items) => (items || []).map((item, i) => String(item?.key ?? i)).join("|");

// The open item index of a chart's tap panel, or null: only an index that
// still exists in a list of `count` items.
export const openIndexIn = (tap, count) => {
  const key = tap?.openKey;
  return Number.isInteger(key) && key >= 0 && key < count ? key : null;
};

export const TapPanel = ({ id, open, onClose, children, placement = "below", bare = false, className = "" }) => (
  <div
    id={id}
    aria-live="polite"
    onKeyDown={(e) => {
      if (e.key === "Escape" && open) {
        e.preventDefault();
        onClose?.();
      }
    }}
    className={cx(open ? (placement === "above" ? "pb-[10px]" : "pt-[10px]") : null, className)}
  >
    {open && (bare ? children : <div className="flex flex-col gap-2 rounded-[12px] bg-bm-subtle p-3">{children}</div>)}
  </div>
);

export default TapPanel;
